import { MoreHorizontal, Plus } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { cn } from '@/components/ui/utils'
import { registerShortcuts } from '@/lib/keys'
import { useEditorStore } from '@/stores/editorStore'
import { useKindPrefs } from '@/stores/vocabStore'

import { KindIcon, performPick, useKindUi } from './KindPicker'
import {
  defaultKind,
  describeKind,
  digitShortcuts,
  DOMAIN_WORDS,
  domainOfFeature,
  domainOfSelection,
  domainOfTool,
  featuresOf,
  kindColour,
  kindCounts,
  kindLabel,
  SLOT_OF,
  STAGE_DOMAINS,
  useVocab,
  type StageDomain,
} from './vocab'

/**
 * The kind bar (§3.2, §9.3): the four lists — Shapes, Spots, Ways in and out, Paths — with the
 * first nine kinds of the current list on the number keys, "More…" for the searchable picker and
 * "+ New" for a kind of your own.
 *
 * The list follows what you are doing: the selection's list, else the drawing tool's, else the
 * tab you picked. The digits are **always** kind keys (never zoom presets) and they only ever
 * touch features of the list on screen — pressing `1` with a spot selected can no longer turn it
 * into "sky". With nothing selected a digit (or a click) gets you ready to add that kind: it arms
 * it and picks the matching tool.
 */

export interface KindLegendProps {
  className?: string
}

const TAB_LABEL: Record<StageDomain, string> = {
  region_kind: 'Shapes',
  anchor_kind: 'Spots',
  portal_kind: 'Ways in/out',
  path_kind: 'Paths',
}

export function KindLegend({ className }: KindLegendProps) {
  const vocab = useVocab()
  const doc = useEditorStore((s) => s.doc)
  const selection = useEditorStore((s) => s.selection)
  const tool = useEditorStore((s) => s.tool)
  const activeKind = useEditorStore((s) => s.activeKind)
  const setActiveKind = useEditorStore((s) => s.setActiveKind)
  const pinsAll = useKindPrefs((s) => s.pins)
  const openPicker = useKindUi((s) => s.openPicker)
  const openNewKind = useKindUi((s) => s.openNewKind)

  const selDomain = domainOfSelection(doc, selection.ids)
  const toolDomain = domainOfTool(tool)
  const [tab, setTab] = useState<StageDomain>(selDomain ?? toolDomain ?? 'region_kind')
  useEffect(() => {
    if (selDomain) setTab(selDomain)
  }, [selDomain])
  useEffect(() => {
    if (toolDomain) setTab(toolDomain)
  }, [toolDomain])

  const domain = tab
  const words = DOMAIN_WORDS[domain]
  const slot = SLOT_OF[domain]
  const keys = useMemo(() => digitShortcuts(vocab, domain, pinsAll[domain] ?? []), [vocab, domain, pinsAll])
  const counts = useMemo(() => kindCounts(doc, domain), [doc, domain])
  const selectedHere = selection.ids.filter((id) => domainOfFeature(doc, id) === domain)
  const selectedKinds = new Set(featuresOf(doc, domain).filter((f) => selectedHere.includes(f.id)).map((f) => f.kind))
  const current = selectedHere.length ? (selectedKinds.size === 1 ? [...selectedKinds][0] : undefined) : activeKind[slot]

  // Arm a sensible kind in every list before anyone picks one (the Spot tool used to refuse to
  // place anything until a spot kind was chosen from a list that only showed shapes).
  useEffect(() => {
    for (const d of STAGE_DOMAINS) {
      const s = SLOT_OF[d]
      const armed = useEditorStore.getState().activeKind[s]
      const ok = armed ? vocab.get(armed, d)?.enabled === true : false
      if (!ok) {
        const fallback = defaultKind(vocab, d)
        if (fallback && fallback !== armed) setActiveKind(s, fallback)
      }
    }
  }, [vocab, setActiveKind])

  const apply = (id: string) => {
    const ed = useEditorStore.getState()
    const here = ed.selection.ids.filter((sid) => domainOfFeature(ed.doc, sid) === domain)
    if (here.length) {
      ed.setActiveKind(slot, id)
      performPick(domain, id, { kind: 'set', ids: here })
      return
    }
    performPick(domain, id, { kind: 'arm' })
  }

  // `1–9` and `/`, registered in the editor scope so they stay quiet in inputs and dialogs. The
  // handlers read the latest list through a ref instead of re-binding every render.
  const latest = useRef({ keys, apply, domain })
  latest.current = { keys, apply, domain }
  // Each digit is bound by character and by physical key (`Digit3`), so layouts whose number row
  // types other characters unshifted (AZERTY: & é " …) still reach the kind keys.
  useEffect(() => {
    const bindings: Record<string, (e: KeyboardEvent) => void> = {}
    for (let n = 1; n <= 9; n++) {
      const key = String(n)
      const pick = () => {
        const hit = latest.current.keys.find((d) => d.key === key)
        if (hit) latest.current.apply(hit.kind.id)
      }
      bindings[key] = pick
      bindings[`Digit${key}`] = pick
    }
    const open = () => useKindUi.getState().openPicker(latest.current.domain, { kind: 'arm' })
    bindings['/'] = open
    // Where "/" needs Shift (Russian: Shift + \), the press is Shift + "/"; on US that is "?".
    bindings['Shift+/'] = open
    return registerShortcuts('editor', bindings)
  }, [])

  const docCount = (d: StageDomain) => featuresOf(doc, d).length

  return (
    <section aria-label="Kinds" className={cn('flex flex-col gap-2', className)} data-testid="kind-bar" data-domain={domain}>
      {/* Each tab as wide as its words (not four equal quarters, which cut "Ways in/out"); wraps if it must. */}
      <div role="tablist" aria-label="Which list" className="flex min-w-0 flex-wrap gap-0.5 rounded-lg bg-paper-2 p-0.5">
        {STAGE_DOMAINS.map((d) => (
          <button
            key={d}
            type="button"
            role="tab"
            aria-selected={d === domain}
            onClick={() => setTab(d)}
            className={cn(
              'min-w-0 flex-auto truncate whitespace-nowrap rounded-md px-1.5 py-1 text-[11px] font-medium text-ink-2 transition-colors focus-visible:focus-ring',
              d === domain ? 'bg-paper text-ink shadow-card' : 'hover:text-ink',
            )}
            title={`${DOMAIN_WORDS[d].title} (${docCount(d)} on this picture)`}
          >
            {TAB_LABEL[d]}
            {docCount(d) ? <span className="ml-0.5 text-ink-3">{docCount(d)}</span> : null}
          </button>
        ))}
      </div>

      <ul className="flex min-w-0 flex-wrap gap-1" aria-label={`${words.title} on the number keys`}>
        {keys.map(({ key, kind }) => {
          const count = counts.get(kind.id) ?? 0
          const selected = current === kind.id
          const label = kindLabel(vocab, kind.id, domain)
          return (
            <li key={kind.id}>
              <button
                type="button"
                aria-pressed={selected}
                onClick={() => apply(kind.id)}
                title={`${label} — press ${key}.${count ? ` ${count} on this picture.` : ''} ${describeKind(kind).join(' ')}`}
                data-kind={kind.id}
                className={cn(
                  'inline-flex h-7 max-w-[10rem] items-center gap-1.5 rounded-full border pl-2 pr-1 text-xs font-medium transition-colors focus-visible:focus-ring',
                  selected ? 'border-transparent bg-accent-soft text-ink' : 'border-line bg-paper text-ink-2 hover:bg-paper-2 hover:text-ink',
                )}
              >
                <KindIcon name={kind.icon} className="size-3.5 shrink-0" style={{ color: kindColour(vocab, kind.id, domain) }} />
                <span className="truncate">{label}</span>
                <kbd className="rounded bg-paper-3 px-1 font-mono text-[10px] text-ink-2">{key}</kbd>
              </button>
            </li>
          )
        })}
        <li>
          <button
            type="button"
            onClick={() => openPicker(domain, selectedHere.length ? { kind: 'set', ids: selectedHere } : { kind: 'arm' })}
            className="inline-flex h-7 items-center gap-1 rounded-full border border-dashed border-line px-2.5 text-xs font-medium text-ink-2 hover:bg-paper-2 hover:text-ink focus-visible:focus-ring"
            title="Search every kind (/)"
          >
            <MoreHorizontal className="size-3.5" aria-hidden />
            All {words.many}
            <kbd className="rounded bg-paper-3 px-1 font-mono text-[10px]">/</kbd>
          </button>
        </li>
        <li>
          <button
            type="button"
            onClick={() => openNewKind(domain, selectedHere.length ? { kind: 'set', ids: selectedHere } : { kind: 'arm' })}
            className="inline-flex h-7 items-center gap-1 rounded-full border border-dashed border-line px-2.5 text-xs font-medium text-ink-2 hover:bg-paper-2 hover:text-ink focus-visible:focus-ring"
          >
            <Plus className="size-3.5" aria-hidden />
            New {words.one}
          </button>
        </li>
      </ul>

      {/* Keyboard advice, so not on a phone-sized screen (the room goes to the lists there). */}
      <p className="hidden text-xs text-ink-3 sm:block">
        {selectedHere.length
          ? `Press a number (or click) to change what the selected ${selectedHere.length === 1 ? words.one : words.many} ${selectedHere.length === 1 ? 'is' : 'are'}.`
          : `Press a number (or click) to choose the ${words.one} you add next, then ${domain === 'portal_kind' ? 'click near a side of the picture or on a door' : 'add it on the picture'}.`}
      </p>
    </section>
  )
}
